import { useEffect, useSyncExternalStore, type RefObject } from 'react';

/**
 * FRONT-2669 — drawn diagrams in the Canvas document Preview. After the Markdown
 * renders, every fenced ```mermaid block (`marked` emits
 * `<pre><code class="language-mermaid">`) is replaced by the SVG mermaid draws
 * from it. Mermaid is a large library, so it is loaded ONLY through a dynamic
 * `import('mermaid')` the first time a block is actually present — a document
 * with no diagram never downloads it.
 */

/** A fence that has not been drawn yet. */
export const MERMAID_BLOCK_SELECTOR = 'pre > code.language-mermaid';
/** A diagram already drawn: its source is kept here so a theme change can redraw it. */
export const DIAGRAM_SOURCE_ATTR = 'data-mermaid-source';
export const DIAGRAM_FAILED_NOTE = "This diagram couldn't be drawn — showing its source.";

/** The one configuration every render uses: no auto-scan, strict sanitising, theme-matched. */
export function mermaidInitConfig(dark: boolean) {
  return {
    startOnLoad: false,
    securityLevel: 'strict' as const,
    theme: dark ? ('dark' as const) : ('default' as const),
    // A failed render throws instead of painting mermaid's own error graphic into the page.
    suppressErrorRendering: true,
    // Labels as SVG <text>, never <foreignObject> HTML: the sanitiser below keeps the SVG profile only,
    // and a foreignObject label would be dropped by it. Both keys: the flowchart's own and the global one.
    htmlLabels: false,
    flowchart: { htmlLabels: false },
  };
}

/**
 * Defence in depth: mermaid's own `securityLevel: 'strict'` already sanitises, and its
 * output still goes through DOMPurify's SVG profiles here before it touches the page.
 */
export const SVG_SANITIZE_CONFIG = { USE_PROFILES: { svg: true, svgFilters: true } } as const;

let counter = 0;
/** A fresh element id per render call (mermaid ids must be unique in the document and start with a letter). */
export function nextDiagramId(): string {
  counter += 1;
  return `canvas-mermaid-${counter}`;
}

/** What to draw: the source text of an undrawn fence (`code`) or an already drawn diagram (`div`). */
function sourceOf(target: Element): string {
  return target.hasAttribute(DIAGRAM_SOURCE_ATTR) ? (target.getAttribute(DIAGRAM_SOURCE_ATTR) ?? '') : (target.textContent ?? '');
}

/**
 * The words a person reads in a diagram: the text of its label elements, whitespace
 * normalised, one entry per word. Taken from an INERT document (DOMParser), never from
 * a live element, so parsing the unsanitised markup cannot load or run anything.
 */
function visibleWords(svg: string): string[] {
  const doc = new DOMParser().parseFromString(svg, 'text/html');
  const text = [...doc.querySelectorAll('text, foreignObject')].map((n) => n.textContent ?? '').join(' ');
  return text.split(/\s+/).filter(Boolean);
}

/**
 * True when sanitising removed label text that was in mermaid's own output. Compared as
 * word counts, so a dropped label shows up even when the same word appears elsewhere. The
 * sanitised diagram would then be drawn with missing labels, which is worse than showing
 * the source, so the caller falls back to the source instead.
 */
export function droppedLabelText(beforeWords: readonly string[], afterWords: readonly string[]): boolean {
  const left = new Map<string, number>();
  for (const w of afterWords) left.set(w, (left.get(w) ?? 0) + 1);
  for (const w of beforeWords) {
    const n = left.get(w) ?? 0;
    if (n === 0) return true;
    left.set(w, n - 1);
  }
  return false;
}

/** The sanitised SVG, or null when sanitising dropped visible label text. */
function sanitizedSvgOrNull(svg: string, purify: { sanitize: (dirty: string, cfg: object) => string }): string | null {
  const clean = purify.sanitize(svg, SVG_SANITIZE_CONFIG);
  return droppedLabelText(visibleWords(svg), visibleWords(clean)) ? null : clean;
}

function buildDiagram(source: string, cleanSvg: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.setAttribute('data-testid', 'canvas-diagram');
  wrap.setAttribute('role', 'img');
  wrap.setAttribute('aria-label', 'Diagram');
  wrap.setAttribute(DIAGRAM_SOURCE_ATTR, source);
  wrap.className = 'my-4 flex justify-center overflow-x-auto';
  wrap.innerHTML = cleanSvg;
  return wrap;
}

function showFailure(fence: Element): void {
  const pre = fence.closest('pre');
  if (!pre || pre.previousElementSibling?.getAttribute('data-testid') === 'canvas-diagram-note') return;
  const note = document.createElement('p');
  note.setAttribute('data-testid', 'canvas-diagram-note');
  note.className = 'mb-1 text-xs text-muted-foreground';
  note.textContent = DIAGRAM_FAILED_NOTE;
  pre.before(note);
}

async function drawAll(root: HTMLElement, dark: boolean, isStale: () => boolean): Promise<void> {
  const targets = [...root.querySelectorAll(`${MERMAID_BLOCK_SELECTOR}, [${DIAGRAM_SOURCE_ATTR}]`)];
  if (targets.length === 0) return;
  let mermaid: typeof import('mermaid').default;
  let purify: typeof import('dompurify').default;
  try {
    // Both are loaded here, in the lazy path, never statically.
    [mermaid, purify] = await Promise.all([import('mermaid').then((m) => m.default), import('dompurify').then((m) => m.default)]);
  } catch {
    if (!isStale()) targets.forEach((t) => t.matches(MERMAID_BLOCK_SELECTOR) && showFailure(t));
    return;
  }
  if (isStale()) return;
  mermaid.initialize(mermaidInitConfig(dark));
  for (const target of targets) {
    if (isStale()) return;
    const source = sourceOf(target);
    const id = nextDiagramId();
    try {
      const { svg } = await mermaid.render(id, source);
      // The document may have changed while mermaid worked: discard, touch nothing.
      if (isStale() || !target.isConnected) return;
      const cleanSvg = sanitizedSvgOrNull(svg, purify);
      // Sanitising dropped label text: the fallback (source + note) beats a diagram without its labels.
      if (cleanSvg === null) throw new Error('sanitised diagram lost its labels');
      const drawn = buildDiagram(source, cleanSvg);
      if (target.hasAttribute(DIAGRAM_SOURCE_ATTR)) target.replaceWith(drawn);
      else (target.closest('pre') ?? target).replaceWith(drawn);
    } catch {
      document.getElementById(id)?.remove();
      document.getElementById(`d${id}`)?.remove();
      if (!isStale() && target.isConnected && target.matches(MERMAID_BLOCK_SELECTOR)) showFailure(target);
    }
  }
}

/**
 * Draw the mermaid fences under `root`. Re-runs when the document text or the
 * theme changes; an earlier run still waiting on mermaid is discarded the moment
 * a newer one starts (or the view unmounts), so a stale render can never land.
 */
export function useMermaidBlocks(root: RefObject<HTMLElement | null>, content: string, dark: boolean): void {
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    let stale = false;
    void drawAll(el, dark, () => stale);
    return () => {
      stale = true;
    };
  }, [root, content, dark]);
}

function subscribeToTheme(onChange: () => void): () => void {
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => {};
  // The theme store paints the theme as a `dark` / `light` class on <html>; that class IS the painted theme.
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => observer.disconnect();
}

/**
 * Whether the page is painted dark right now, read from the `dark` class on <html>
 * (what the theme store writes), so a diagram can be redrawn when the theme flips.
 * Reading the painted class rather than the store keeps this free of the store's
 * construction side effects.
 */
export function usePaintedDark(): boolean {
  return useSyncExternalStore(
    subscribeToTheme,
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
    () => false,
  );
}
